'use es6';

import PropTypes from 'prop-types';
import { memoize } from 'underscore';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import HorizontalEdgeToolbar from 'layout-dnd-components/DndToolbar/HorizontalEdgeToolbar';
import { updateSelectedItem } from 'ContentEditorUI/redux/actions/selectionActions';
import { cloneColumnFromLayoutSection, requestCloneFromLayoutSection, deleteColumnFromLayoutSection } from 'ContentEditorUI/redux/actions/moduleActions';
import { COLUMN } from 'ContentEditorUI/constants/cloneTypes';
import { logLayoutSectionEvent } from 'InpageEditorUI/eventsAsActions/layoutTransitionEventsAsActions';
import { LAYOUT_SECTION_TRACKING } from 'ContentEditorUI/lib/layoutSection/constants';
import { startGeneratedHublRequest } from 'ContentEditorUI/redux/actions/hublActions';
import { hashFunctionForObject, styledIconButtonComponent, styledHandleButtonComponent } from './helpers';
import ColumnIcon from './ColumnIcon';
import { jsx as _jsx } from "react/jsx-runtime";
export const buildStyleColumnAction = (columnName, layoutSectionId, dispatch, bus, {
  onlyInDropdown
} = {}) => ({
  id: 'column-styles',
  icon: 'edit',
  menuLabel: /*#__PURE__*/_jsx(FormattedMessage, {
    message: "inpage.actionToolbar.column.editStyles"
  }),
  tooltipLabel: /*#__PURE__*/_jsx(FormattedMessage, {
    message: "inpage.actionToolbar.column.editStyles"
  }),
  onlyInDropdown,
  callback: () => {
    dispatch(updateSelectedItem(columnName, {
      containerId: layoutSectionId
    }));
    bus.dispatchActionToEventBus(logLayoutSectionEvent(LAYOUT_SECTION_TRACKING.EDIT_LAYOUT_STYLES, {
      type: 'COLUMN'
    }));
  }
});
export const buildDuplicateColumnAction = (columnName, layoutSectionId, dispatch, bus, {
  onlyInDropdown,
  canBeCloned
} = {}) => {
  const menuLabel = /*#__PURE__*/_jsx(FormattedMessage, {
    message: "inpage.actionToolbar.column.clone"
  });
  let menuTooltip = undefined;
  let tooltipLabel = menuLabel;
  if (!canBeCloned) {
    menuTooltip = /*#__PURE__*/_jsx(FormattedMessage, {
      message: "inpage.actionToolbar.column.cantCloneDetail"
    });
    tooltipLabel = /*#__PURE__*/_jsx(FormattedMessage, {
      message: "inpage.actionToolbar.column.cantCloneDetail"
    });
  }
  return {
    id: 'column-duplicate',
    icon: 'duplicate',
    menuLabel,
    menuTooltip,
    tooltipLabel,
    onlyInDropdown,
    disabled: !canBeCloned,
    callback: () => {
      bus.dispatchActionToEventBus(logLayoutSectionEvent(LAYOUT_SECTION_TRACKING.CLONE_COLUMN));
      dispatch(requestCloneFromLayoutSection(columnName, layoutSectionId, COLUMN, cloneColumnFromLayoutSection(columnName, layoutSectionId)));
    }
  };
};
export const buildDeleteColumnAction = (columnName, layoutSectionId, dispatch, bus, {
  onlyInDropdown
} = {}) => ({
  id: 'column-delete',
  icon: 'delete',
  menuLabel: /*#__PURE__*/_jsx(FormattedMessage, {
    message: "inpage.actionToolbar.column.delete"
  }),
  tooltipLabel: /*#__PURE__*/_jsx(FormattedMessage, {
    message: "inpage.actionToolbar.column.delete"
  }),
  onlyInDropdown,
  callback: () => {
    bus.dispatchActionToEventBus(logLayoutSectionEvent(LAYOUT_SECTION_TRACKING.DELETE_COLUMN));
    dispatch(deleteColumnFromLayoutSection(columnName, layoutSectionId));
  }
});
const buildCopyAsHublAction = (columnName, layoutSectionId, dispatch) => ({
  id: 'copy-as-hubl',
  icon: 'customHubl',
  menuLabel: /*#__PURE__*/_jsx(FormattedMessage, {
    message: "inpage.actionToolbar.copy"
  }),
  menuTooltip: /*#__PURE__*/_jsx(FormattedMessage, {
    message: "inpage.actionToolbar.copy"
  }),
  onlyInDropdown: true,
  callback: () => {
    dispatch(startGeneratedHublRequest(columnName, layoutSectionId));
  }
});
const buildHideOnMobileAction = (bus, toggleNodeVisibilityOnThisBreakpoint) => ({
  id: 'column-hide',
  icon: 'hide',
  iconInstanceSize: 13,
  menuLabel: /*#__PURE__*/_jsx(FormattedMessage, {
    message: "inpage.actionToolbar.column.hide"
  }),
  tooltipLabel: /*#__PURE__*/_jsx(FormattedMessage, {
    message: "inpage.actionToolbar.column.hide"
  }),
  callback: e => {
    bus.dispatchActionToEventBus(logLayoutSectionEvent(LAYOUT_SECTION_TRACKING.HIDE_COLUMN));
    toggleNodeVisibilityOnThisBreakpoint(false, e);
  }
});
export const buildActionsForColumn = memoize(({
  columnName,
  layoutSectionId,
  isInDefaultBreakpointMode,
  canBeCloned,
  dispatch,
  bus,
  isInDeveloperMode,
  toggleNodeVisibilityOnThisBreakpoint
}) => {
  const actions = [buildStyleColumnAction(columnName, layoutSectionId, dispatch, bus, {
    onlyInDropdown: false
  })];
  if (isInDefaultBreakpointMode) {
    actions.push(buildDuplicateColumnAction(columnName, layoutSectionId, dispatch, bus, {
      onlyInDropdown: false,
      canBeCloned
    }), buildDeleteColumnAction(columnName, layoutSectionId, dispatch, bus, {
      onlyInDropdown: false
    }));
    if (isInDeveloperMode) {
      actions.push(buildCopyAsHublAction(columnName, layoutSectionId, dispatch));
    }
  } else {
    actions.push(buildHideOnMobileAction(bus, toggleNodeVisibilityOnThisBreakpoint));
  }
  return actions;
}, hashFunctionForObject);
export const BaseResponsiveColumnToolbar = ({
  active,
  style,
  actions,
  onHandleMouseDown,
  onDropdownOpenChange,
  responsiveSize,
  connectDragSource,
  showHandleGrippie,
  isSelectedItem
}) => {
  const buttonActions = [...actions];
  const styleActionIndex = buttonActions.findIndex(action => action.id === 'column-styles');

  // Assume style action button's callback should be used when clicking on the "icon" of this toolbar
  const iconClickHandler = styleActionIndex === -1 || buttonActions[styleActionIndex].disabled ? null : buttonActions[styleActionIndex].callback;
  if (isSelectedItem && styleActionIndex !== -1) {
    buttonActions.splice(styleActionIndex, 1);
  }
  const classes = 'hs-ls-responsive-toolbar hs-ls-responsive-column-toolbar' + (!active ? " hs-ls-toolbar-inactive" : "");
  return /*#__PURE__*/_jsx(HorizontalEdgeToolbar, {
    active: active,
    icon: ColumnIcon,
    actions: buttonActions,
    style: style,
    responsiveSize: responsiveSize,
    handleTooltip: I18n.text('inpage.actionToolbar.column.label'),
    onHandleMouseDown: onHandleMouseDown,
    onDropdownOpenChange: onDropdownOpenChange,
    onHandleClick: iconClickHandler,
    className: classes,
    showHandleGrippie: showHandleGrippie,
    renderWrappedHandle: connectDragSource ? ({
      handleButton
    }) => connectDragSource( /*#__PURE__*/_jsx("div", {
      children: handleButton
    })) : undefined,
    StyledIconButton: styledIconButtonComponent,
    StyledHandleButton: styledHandleButtonComponent,
    draggable: true
  });
};
BaseResponsiveColumnToolbar.propTypes = {
  active: PropTypes.bool.isRequired,
  style: PropTypes.object,
  actions: PropTypes.array.isRequired,
  onHandleMouseDown: PropTypes.func,
  onDropdownOpenChange: PropTypes.func,
  responsiveSize: PropTypes.string,
  connectDragSource: PropTypes.func,
  showHandleGrippie: PropTypes.bool,
  isSelectedItem: PropTypes.bool
};